import React, { memo, useMemo } from 'react'
import { Box, Typography, Tooltip, Chip, Paper } from '@mui/material'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'

const TOTAL_HOURS = 168
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// ─── Colour constants ───────────────────────────────────────────────
const COLOUR_PRODUCTION = '#4caf50'
const COLOUR_CIP = '#ff9800'
const COLOUR_PRECLEAN = '#ffb74d'
const COLOUR_DOWNTIME = '#ef5350'
const COLOUR_IDLE = '#f5f5f5'

// ─── Helpers ─────────────────────────────────────────────────────────

/** Parse "(0,8),(24,32)" into [number, number][] */
function parseDowntimeIntervals(
  s: string | null | undefined
): [number, number][] {
  if (!s) return []
  const intervals: [number, number][] = []
  const re = /\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(s)) !== null) {
    intervals.push([Math.round(parseFloat(m[1])), Math.round(parseFloat(m[2]))])
  }
  return intervals
}

type SlotType = 'production' | 'cip' | 'preclean' | 'downtime' | 'idle'

/**
 * Build a SlotType[168] array for a set of rows in a given mode.
 * Priority: production > cip > preclean > idle
 */
function buildSlotArray(
  rows: InputRow[],
  mode: 'kitchen' | 'line',
  downtimeStr?: string | null
): SlotType[] {
  const production = new Uint8Array(TOTAL_HOURS)
  const cip = new Uint8Array(TOTAL_HOURS)
  const preclean = new Uint8Array(TOTAL_HOURS)
  const downtime = new Uint8Array(TOTAL_HOURS)

  for (const row of rows) {
    const prodStart =
      mode === 'kitchen' ? row.kitchen_start_time : row.line_start_time
    const prodEnd =
      mode === 'kitchen' ? row.kitchen_end_time : row.line_end_time
    const cipStart =
      mode === 'kitchen'
        ? row.kitchen_clean_start_time
        : row.line_clean_start_time
    const cipEnd =
      mode === 'kitchen' ? row.kitchen_clean_end_time : row.line_clean_end_time
    const precleanStart =
      mode === 'kitchen'
        ? row.kitchen_preclean_start_time
        : row.line_preclean_start_time
    const precleanEnd =
      mode === 'kitchen'
        ? row.kitchen_preclean_end_time
        : row.line_preclean_end_time

    for (
      let h = Math.max(0, Math.floor(prodStart));
      h < Math.min(TOTAL_HOURS, Math.ceil(prodEnd));
      h++
    ) {
      production[h] = 1
    }
    for (
      let h = Math.max(0, Math.floor(cipStart));
      h < Math.min(TOTAL_HOURS, Math.ceil(cipEnd));
      h++
    ) {
      cip[h] = 1
    }
    if (
      precleanStart != null &&
      precleanEnd != null &&
      precleanEnd > precleanStart
    ) {
      for (
        let h = Math.max(0, Math.floor(precleanStart));
        h < Math.min(TOTAL_HOURS, Math.ceil(precleanEnd));
        h++
      ) {
        preclean[h] = 1
      }
    }
  }

  // Mark downtime intervals
  for (const [start, end] of parseDowntimeIntervals(downtimeStr)) {
    for (let h = Math.max(0, start); h < Math.min(TOTAL_HOURS, end); h++) {
      downtime[h] = 1
    }
  }

  // Priority: production > cip > preclean > downtime > idle
  const slots: SlotType[] = new Array(TOTAL_HOURS)
  for (let h = 0; h < TOTAL_HOURS; h++) {
    if (production[h]) slots[h] = 'production'
    else if (cip[h]) slots[h] = 'cip'
    else if (preclean[h]) slots[h] = 'preclean'
    else if (downtime[h]) slots[h] = 'downtime'
    else slots[h] = 'idle'
  }
  return slots
}

const slotColour: Record<SlotType, string> = {
  production: COLOUR_PRODUCTION,
  cip: COLOUR_CIP,
  preclean: COLOUR_PRECLEAN,
  downtime: COLOUR_DOWNTIME,
  idle: COLOUR_IDLE,
}

function formatHour(h: number): string {
  const day = DAY_LABELS[Math.floor(h / 24)]
  const hour = h % 24
  return `${day} ${hour.toString().padStart(2, '0')}:00`
}

// ─── MiniGantt ───────────────────────────────────────────────────────

interface MiniGanttProps {
  rows: InputRow[]
  mode: 'kitchen' | 'line'
  downtime?: string | null
}

export const MiniGantt = memo(function MiniGantt({
  rows,
  mode,
  downtime,
}: MiniGanttProps) {
  const { slots, util } = useMemo(() => {
    const s = buildSlotArray(rows, mode, downtime)
    const productionHours = s.filter((t) => t === 'production').length
    return { slots: s, util: Math.round((productionHours / TOTAL_HOURS) * 100) }
  }, [rows, mode, downtime])

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: 24,
        width: '100%',
        gap: '4px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
          borderRadius: '3px',
          overflow: 'hidden',
          border: '1px solid #e0e0e0',
        }}
      >
        {slots.map((slot, h) => (
          <Box
            key={h}
            sx={{
              flex: 1,
              height: '100%',
              backgroundColor: slotColour[slot],
              borderRight:
                h > 0 && h % 24 === 0 ? '1px solid rgba(0,0,0,0.15)' : 'none',
              minWidth: 0,
            }}
          />
        ))}
      </Box>
      <Typography
        sx={{
          fontSize: '0.6rem',
          fontWeight: 600,
          color: 'text.secondary',
          whiteSpace: 'nowrap',
          minWidth: 28,
          textAlign: 'right',
        }}
      >
        {util}%
      </Typography>
    </Box>
  )
})

// ─── ResourceGantt ───────────────────────────────────────────────────

interface ResourceGanttProps {
  rows: InputRow[]
  mode: 'kitchen' | 'line'
  resourceName: string
}

/** Percentage position helper */
const pct = (hours: number) => (hours / TOTAL_HOURS) * 100

export const ResourceGantt = memo(function ResourceGantt({
  rows,
  mode,
  resourceName,
}: ResourceGanttProps) {
  const stats = useMemo(() => {
    let productionHours = 0
    let cleaningHours = 0
    const productCodes = new Set<string>()

    for (const row of rows) {
      const pStart =
        mode === 'kitchen' ? row.kitchen_start_time : row.line_start_time
      const pEnd = mode === 'kitchen' ? row.kitchen_end_time : row.line_end_time
      productionHours += Math.max(0, pEnd - pStart)

      const cStart =
        mode === 'kitchen'
          ? row.kitchen_clean_start_time
          : row.line_clean_start_time
      const cEnd =
        mode === 'kitchen'
          ? row.kitchen_clean_end_time
          : row.line_clean_end_time
      cleaningHours += Math.max(0, cEnd - cStart)

      const pcStart =
        mode === 'kitchen'
          ? row.kitchen_preclean_start_time
          : row.line_preclean_start_time
      const pcEnd =
        mode === 'kitchen'
          ? row.kitchen_preclean_end_time
          : row.line_preclean_end_time
      if (pcStart != null && pcEnd != null && pcEnd > pcStart) {
        cleaningHours += pcEnd - pcStart
      }

      productCodes.add(row.productCode)
    }

    const util = Math.round(
      ((productionHours + cleaningHours) / TOTAL_HOURS) * 100
    )
    return {
      productionHours: Math.round(productionHours * 10) / 10,
      cleaningHours: Math.round(cleaningHours * 10) / 10,
      util,
      productCount: productCodes.size,
    }
  }, [rows, mode])

  /** Production bars sorted by start time */
  const productionBars = useMemo(() => {
    return rows
      .map((row) => {
        const start =
          mode === 'kitchen' ? row.kitchen_start_time : row.line_start_time
        const end =
          mode === 'kitchen' ? row.kitchen_end_time : row.line_end_time
        return {
          start,
          end,
          productCode: row.productCode,
          productDescription:
            mode === 'kitchen'
              ? (row.base_material_description ?? '')
              : (row.product_description ?? ''),
          quantity: row.quantity,
          runRate: mode === 'kitchen' ? row.runRateBase : row.runRateLine,
          baseMaterial: row.base_material_description,
        }
      })
      .sort((a, b) => a.start - b.start)
  }, [rows, mode])

  /** CIP bars */
  const cipBars = useMemo(() => {
    return rows
      .map((row) => {
        const start =
          mode === 'kitchen'
            ? row.kitchen_clean_start_time
            : row.line_clean_start_time
        const end =
          mode === 'kitchen'
            ? row.kitchen_clean_end_time
            : row.line_clean_end_time
        const cipName = mode === 'kitchen' ? row.kitchenCIP : row.lineCIP
        return { start, end, cipName }
      })
      .filter((b) => b.end > b.start)
      .sort((a, b) => a.start - b.start)
  }, [rows, mode])

  /** Preclean bars */
  const precleanBars = useMemo(() => {
    return rows
      .map((row) => {
        const start =
          mode === 'kitchen'
            ? row.kitchen_preclean_start_time
            : row.line_preclean_start_time
        const end =
          mode === 'kitchen'
            ? row.kitchen_preclean_end_time
            : row.line_preclean_end_time
        const cipName =
          mode === 'kitchen' ? row.kitchenPrecleanCIP : row.linePrecleanCIP
        return { start, end, cipName }
      })
      .filter((b) => b.start != null && b.end != null && b.end > b.start)
      .sort((a, b) => a.start - b.start)
  }, [rows, mode])

  /**
   * Assign vertical lanes to production bars to handle overlaps.
   * Returns bars with a `lane` index.
   */
  const lanedProductionBars = useMemo(() => {
    const laneEnds: number[] = []
    return productionBars.map((bar) => {
      let lane = laneEnds.findIndex((end) => end <= bar.start)
      if (lane === -1) {
        lane = laneEnds.length
        laneEnds.push(bar.end)
      } else {
        laneEnds[lane] = bar.end
      }
      return { ...bar, lane }
    })
  }, [productionBars])

  const productionLaneCount = useMemo(
    () =>
      lanedProductionBars.length > 0
        ? Math.max(...lanedProductionBars.map((b) => b.lane)) + 1
        : 1,
    [lanedProductionBars]
  )

  const PRODUCTION_ROW_HEIGHT = 22
  const CLEANING_ROW_HEIGHT = 16
  const DAY_HEADER_HEIGHT = 20
  const HOUR_TICK_AREA = 14
  const STATS_HEIGHT = 32
  const PADDING = 8

  const productionAreaHeight = productionLaneCount * (PRODUCTION_ROW_HEIGHT + 2)
  const cleaningAreaHeight =
    cipBars.length + precleanBars.length > 0 ? CLEANING_ROW_HEIGHT + 4 : 0
  const timelineHeight =
    DAY_HEADER_HEIGHT +
    HOUR_TICK_AREA +
    productionAreaHeight +
    cleaningAreaHeight
  const totalHeight = Math.max(
    120,
    STATS_HEIGHT + PADDING + timelineHeight + PADDING
  )

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        minHeight: 120,
        height: totalHeight,
        overflow: 'hidden',
      }}
    >
      {/* ── Summary stats ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 0.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
          {resourceName}
        </Typography>
        <Chip
          label={`Prod: ${stats.productionHours}h`}
          size="small"
          sx={{
            fontSize: '0.65rem',
            height: 18,
            backgroundColor: COLOUR_PRODUCTION,
            color: '#fff',
          }}
        />
        <Chip
          label={`Clean: ${stats.cleaningHours}h`}
          size="small"
          sx={{
            fontSize: '0.65rem',
            height: 18,
            backgroundColor: COLOUR_CIP,
            color: '#fff',
          }}
        />
        <Chip
          label={`Util: ${stats.util}%`}
          size="small"
          sx={{ fontSize: '0.65rem', height: 18 }}
        />
        <Chip
          label={`${stats.productCount} product${stats.productCount !== 1 ? 's' : ''}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.65rem', height: 18 }}
        />
      </Box>

      {/* ── Timeline container ── */}
      <Box sx={{ position: 'relative', width: '100%', height: timelineHeight }}>
        {/* Day headers */}
        <Box sx={{ display: 'flex', height: DAY_HEADER_HEIGHT }}>
          {DAY_LABELS.map((day) => (
            <Box
              key={day}
              sx={{
                flex: 1,
                textAlign: 'center',
                borderBottom: '1px solid #ccc',
                borderRight: '1px solid #e0e0e0',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  lineHeight: `${DAY_HEADER_HEIGHT}px`,
                }}
              >
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Hour tick area + bar area (relative container for absolute bars) */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: timelineHeight - DAY_HEADER_HEIGHT,
          }}
        >
          {/* Hour grid lines */}
          {Array.from({ length: TOTAL_HOURS }, (_, h) => (
            <Box
              key={`tick-${h}`}
              sx={{
                position: 'absolute',
                left: `${pct(h)}%`,
                top: 0,
                bottom: 0,
                width: '1px',
                backgroundColor:
                  h % 24 === 0
                    ? 'rgba(0,0,0,0.20)'
                    : h % 6 === 0
                      ? 'rgba(0,0,0,0.10)'
                      : 'rgba(0,0,0,0.04)',
                zIndex: 0,
              }}
            />
          ))}

          {/* 6-hour labels */}
          {Array.from({ length: TOTAL_HOURS / 6 }, (_, i) => {
            const h = i * 6
            const hourInDay = h % 24
            return (
              <Typography
                key={`label-${h}`}
                sx={{
                  position: 'absolute',
                  left: `${pct(h)}%`,
                  top: 0,
                  fontSize: '0.55rem',
                  color: 'text.disabled',
                  transform: 'translateX(-50%)',
                  lineHeight: `${HOUR_TICK_AREA}px`,
                  zIndex: 1,
                  userSelect: 'none',
                }}
              >
                {hourInDay.toString().padStart(2, '0')}
              </Typography>
            )
          })}

          {/* Production bars */}
          {lanedProductionBars.map((bar, idx) => {
            const left = pct(bar.start)
            const width = pct(bar.end - bar.start)
            const top = HOUR_TICK_AREA + bar.lane * (PRODUCTION_ROW_HEIGHT + 2)
            return (
              <Tooltip
                key={`prod-${idx}`}
                title={
                  <Box sx={{ fontSize: '0.7rem' }}>
                    <div>
                      <strong>
                        {bar.productDescription || bar.productCode}
                      </strong>
                    </div>
                    {bar.productDescription && (
                      <div style={{ opacity: 0.6, fontSize: '0.65rem' }}>
                        {bar.productCode}
                      </div>
                    )}
                    <div>Qty: {bar.quantity}</div>
                    <div>
                      {formatHour(Math.floor(bar.start))} &ndash;{' '}
                      {formatHour(Math.ceil(bar.end))}
                    </div>
                    <div>Run rate: {bar.runRate}</div>
                    <div>{bar.baseMaterial}</div>
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${left}%`,
                    width: `${width}%`,
                    top,
                    height: PRODUCTION_ROW_HEIGHT,
                    backgroundColor: COLOUR_PRODUCTION,
                    borderRadius: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    zIndex: 2,
                    cursor: 'default',
                    '&:hover': { filter: 'brightness(1.1)' },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: '#fff',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      px: '2px',
                      lineHeight: 1,
                    }}
                  >
                    {bar.productCode}
                  </Typography>
                </Box>
              </Tooltip>
            )
          })}

          {/* CIP cleaning bars */}
          {cipBars.map((bar, idx) => {
            const left = pct(bar.start)
            const width = pct(bar.end - bar.start)
            const top = HOUR_TICK_AREA + productionAreaHeight + 2
            return (
              <Tooltip
                key={`cip-${idx}`}
                title={
                  <Box sx={{ fontSize: '0.7rem' }}>
                    <div>CIP: {bar.cipName}</div>
                    <div>
                      {formatHour(Math.floor(bar.start))} &ndash;{' '}
                      {formatHour(Math.ceil(bar.end))}
                    </div>
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${left}%`,
                    width: `${width}%`,
                    top,
                    height: CLEANING_ROW_HEIGHT,
                    backgroundColor: COLOUR_CIP,
                    borderRadius: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    zIndex: 2,
                    cursor: 'default',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.55rem',
                      color: '#fff',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      px: '2px',
                      lineHeight: 1,
                    }}
                  >
                    {bar.cipName}
                  </Typography>
                </Box>
              </Tooltip>
            )
          })}

          {/* Preclean bars */}
          {precleanBars.map((bar, idx) => {
            const left = pct(bar.start)
            const width = pct(bar.end - bar.start)
            const top =
              HOUR_TICK_AREA +
              productionAreaHeight +
              2 +
              (cipBars.length > 0 ? CLEANING_ROW_HEIGHT + 2 : 0)
            return (
              <Tooltip
                key={`preclean-${idx}`}
                title={
                  <Box sx={{ fontSize: '0.7rem' }}>
                    <div>Preclean: {bar.cipName}</div>
                    <div>
                      {formatHour(Math.floor(bar.start))} &ndash;{' '}
                      {formatHour(Math.ceil(bar.end))}
                    </div>
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${left}%`,
                    width: `${width}%`,
                    top,
                    height: CLEANING_ROW_HEIGHT,
                    backgroundColor: COLOUR_PRECLEAN,
                    borderRadius: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    zIndex: 2,
                    cursor: 'default',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.55rem',
                      color: '#fff',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      px: '2px',
                      lineHeight: 1,
                    }}
                  >
                    {bar.cipName}
                  </Typography>
                </Box>
              </Tooltip>
            )
          })}
        </Box>
      </Box>
    </Paper>
  )
})
